import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import {
  BankAccount,
  BankAccountAccessRight,
  BankAccountAssignableUser,
  BankAccountEmployeeAccess,
  BankAccountStatus,
  BankAccountType,
  Currency,
} from '../../../models/bank-account/bank-account';
import {
  BankAccountAccessFormGroup,
  BankAccountEmployeeAccessFormGroup,
  BankAccountFormGroup,
} from '../../../models/bank-account/bank-account-form';
import { BankStatementLine } from '../../../models/bank-statement/bank-statement';
import {
  BankTransaction,
  BankTransactionMovementType,
  BankTransactionStatus,
  FINANCIAL_COMPANY_ENTITY_CODE,
  INTERNAL_TRANSFER_ENTITY_CODE,
} from '../../../models/bank-transaction/bank-transaction';
import { SubledgerType } from '../../../models/financial-account/financial-account';
import { EnumService } from '../../../services/enum.service';
import { BankTransactionProxyService } from '../../../proxies/bank-transaction.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { BreadcrumbService, SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { reportInvalidFormFields } from '@app/utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

interface EnumOption {
  label: string;
  value: number;
}

type BankTab = 'overview' | 'details' | 'transactions' | 'drafts' | 'access';

@Component({
  selector: 'app-bank-account-details',
  templateUrl: './bank-account-details.component.html',
  styleUrl: './bank-account-details.component.scss',
})
export class BankAccountDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly bankTransactionService = inject(BankTransactionProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  /** The FinancialCompany behind the selected site. A site id is NOT a company id — storing one here makes
   *  every company-scoped lookup downstream (accounting period, chart of accounts) search for a company
   *  that does not exist. */
  private readonly companyReference = signal<string | null>(null);

  isLoading = signal(0);

  /** Null while creating. Signals, not plain fields — the template variants read them, and a `computed`
   *  over a plain field never invalidates when the same route is reused with a different `?id`. */
  readonly currentId = signal<string | null>(null);
  readonly isNew = signal(true);
  readonly headerTitle = signal<string>(this.t('BankAccount_Details_New', 'New Bank Account'));

  /** Header speed-dial: the direction is fixed the moment the movement form opens, so it is chosen
   *  here rather than on a blank form. */
  readonly transactionSpeedDialItems: MenuItem[] = [
    { label: this.t('BankAccount_Txn_AddIn', 'Money in'), icon: 'pi pi-arrow-down-left', command: () => this.addTransaction('INCOME') },
    { label: this.t('BankAccount_Txn_AddOut', 'Money out'), icon: 'pi pi-arrow-up-right', command: () => this.addTransaction('OUTCOME') },
  ];

  readonly activeTab = signal<BankTab>('details');
  readonly account = signal<BankAccount | null>(null);

  typeOptions = signal<EnumOption[]>([]);
  statusOptions = signal<EnumOption[]>([]);
  currencyOptions = signal<EnumOption[]>([]);
  movementTypeOptions = signal<EnumOption[]>([]);

  // ---- statement windows ----
  /** Bounded recent window — drives Overview KPIs + the "recent" list; cheap to load on open. */
  readonly overviewLines = signal<BankStatementLine[]>([]);
  /** Full transaction history — loaded lazily only when the Transactions tab is first opened. */
  readonly transactionLines = signal<BankStatementLine[]>([]);
  private transactionsLoaded = false;
  /** Days of history pulled for the Overview tab (covers the full current month + recent rows). */
  private static readonly OVERVIEW_WINDOW_DAYS = 90;

  /**
   * Unposted movements on this account.
   *
   * <p>Loaded with the account rather than lazily like the statement, because the tab badge is the whole
   * point: a draft is work someone started and left, and it is invisible in the statement — which shows
   * posted lines only. Waiting for a click to count them would hide exactly what needs surfacing.</p>
   */
  readonly draftTransactions = signal<BankTransaction[]>([]);

  // ---- transaction filters (client-side over the loaded statement) ----
  readonly txnSearchControl = new FormControl<string>('', { nonNullable: true });
  readonly txnTypeControl = new FormControl<number | null>(null);
  readonly txnDateRangeControl = new FormControl<Date[] | null>(null);
  private readonly txnSearch = toSignal(this.txnSearchControl.valueChanges, { initialValue: '' });
  private readonly txnType = toSignal(this.txnTypeControl.valueChanges, { initialValue: null });
  private readonly txnDateRange = toSignal(this.txnDateRangeControl.valueChanges, { initialValue: null });

  // ---- draft filter (client-side; drafts are few by nature) ----
  readonly draftSearchControl = new FormControl<string>('', { nonNullable: true });
  private readonly draftSearch = toSignal(this.draftSearchControl.valueChanges, { initialValue: '' });

  readonly draftCount = computed(() => this.draftTransactions().length);

  readonly filteredDrafts = computed(() => {
    const term = (this.draftSearch() ?? '').trim().toLowerCase();
    const drafts = [...this.draftTransactions()].sort((a, b) => (a.transactionDate < b.transactionDate ? 1 : -1));
    if (!term) return drafts;
    return drafts.filter((d) =>
      [d.number, d.bankingReference, d.concept, d.description].some((f) => (f ?? '').toLowerCase().includes(term)),
    );
  });

  /** Expose the enum to the template for the bank GL-account selector. */
  readonly SubledgerType = SubledgerType;
  /** Transient localized GL-account name (display only, not persisted). */
  readonly glAccountNameControl = new FormControl<string | null>(null);

  private typeLabels: Record<number, string> = {};
  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  private movementLabels: Record<number, string> = {};

  fieldLabels: Record<keyof BankAccountFormGroup, string> = {
    code: 'BankAccount_Details_Form_Field_Code',
    name: 'BankAccount_Details_Form_Field_Name',
    bankName: 'BankAccount_Details_Form_Field_BankName',
    bankAccountNumber: 'BankAccount_Details_Form_Field_BankAccountNumber',
    type: 'BankAccount_Details_Form_Field_Type',
    currency: 'BankAccount_Details_Form_Field_Currency',
    status: 'BankAccount_Details_Form_Field_Status',
    country: 'BankAccount_Details_Form_Field_Country',
    swiftBicCode: 'BankAccount_Details_Form_Field_SwiftBic',
    ibanCode: 'BankAccount_Details_Form_Field_Iban',
    glAccountNumber: 'BankAccount_Details_Form_Field_GlAccountNumber',
    glAccountReferenceId: 'BankAccount_Details_Form_Field_GlAccountId',
    accountHolderName: 'BankAccount_Details_Form_Field_HolderName',
    holderPhoneNumber: 'BankAccount_Details_Form_Field_HolderPhone',
    description: 'BankAccount_Details_Form_Field_Description',
    currentBalance: 'BankAccount_Details_Form_Field_CurrentBalance',
    minimumBalance: 'BankAccount_Details_Form_Field_MinimumBalance',
    maximumBalance: 'BankAccount_Details_Form_Field_MaximumBalance',
    ownerUsername: 'BankAccount_Details_Form_Field_Owner',
  };

  readonly bankAccountForm = new FormGroup<BankAccountFormGroup>({
    code: new FormControl<string | null>({ value: null, disabled: true }),
    name: new FormControl<string | null>(null, [Validators.required]),
    bankName: new FormControl<string | null>(null, [Validators.required]),
    bankAccountNumber: new FormControl<string | null>(null, [Validators.required]),
    type: new FormControl<number | null>(BankAccountType.CHECKING, [Validators.required]),
    currency: new FormControl<number | null>(Currency.HNL, [Validators.required]),
    status: new FormControl<number | null>(BankAccountStatus.ACTIVE, [Validators.required]),
    country: new FormControl<string | null>(null, [Validators.required]),
    swiftBicCode: new FormControl<string | null>(null),
    ibanCode: new FormControl<string | null>(null),
    glAccountNumber: new FormControl<string | null>(null),
    glAccountReferenceId: new FormControl<string | null>(null),
    accountHolderName: new FormControl<string | null>(null, [Validators.required]),
    holderPhoneNumber: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    currentBalance: new FormControl<number | null>({ value: 0, disabled: true }),
    minimumBalance: new FormControl<number | null>(0),
    maximumBalance: new FormControl<number | null>(0),
    // Required on create, then permanently disabled — see loadById/patch. Ownership is not
    // transferable in this release, and a disabled control is the honest way to say so.
    ownerUsername: new FormControl<string | null>(null, [Validators.required]),
  });

  // ---------- access list ----------
  /**
   * Edited and saved separately from the account: the API accepts employee grants only through
   * PUT /bank-accounts/{id}/access, so this form has its own dirty state and its own save.
   */
  readonly accessForm = new FormGroup<BankAccountAccessFormGroup>({
    ownerUsername: new FormControl<string | null>(null, [Validators.required]),
    employeeAccess: new FormArray<FormGroup<BankAccountEmployeeAccessFormGroup>>([]),
  });

  private readonly confirmationService = inject(ConfirmationService);

  readonly assignableUsers = signal<BankAccountAssignableUser[]>([]);
  readonly savingAccess = signal(false);

  /** Rights offered per employee row, in the order they widen. */
  readonly accessRightOptions: { label: string; value: BankAccountAccessRight }[] = [
    { label: this.t('BankAccount_Access_ViewBalance', 'View balance'), value: BankAccountAccessRight.VIEW_BALANCE },
    { label: this.t('BankAccount_Access_ViewTransactions', 'View transactions'), value: BankAccountAccessRight.VIEW_TRANSACTIONS },
    { label: this.t('BankAccount_Access_CreateTransaction', 'Create transactions'), value: BankAccountAccessRight.CREATE_TRANSACTION },
    { label: this.t('BankAccount_Access_PostTransaction', 'Post transactions'), value: BankAccountAccessRight.POST_TRANSACTION },
    { label: this.t('BankAccount_Access_Reconcile', 'Reconcile'), value: BankAccountAccessRight.RECONCILE },
    { label: this.t('BankAccount_Access_FullAccess', 'Full access'), value: BankAccountAccessRight.FULL_ACCESS },
  ];

  /**
   * The owner currently SELECTED in the form, which on a pending transfer is not yet the saved one.
   * Tracked as a signal so the employee picker reacts to the transfer before it is saved.
   */
  private readonly selectedOwner = signal<string | null>(null);

  /** True once the form names someone other than the account's saved owner. */
  readonly isTransferringOwnership = computed(() => {
    const saved = this.account()?.ownerUsername ?? null;
    const chosen = this.selectedOwner();
    return !!chosen && !!saved && chosen !== saved;
  });

  /** The outgoing owner's own username, when a transfer is pending — used to offer to keep their access. */
  readonly outgoingOwner = computed(() => (this.isTransferringOwnership() ? (this.account()?.ownerUsername ?? null) : null));

  /** True when a transfer would leave the current owner with no access at all. */
  readonly transferLosesOwnAccess = computed(() => {
    const outgoing = this.outgoingOwner();
    if (!outgoing) return false;
    return !this.employeeRows().some((r) => r.controls.username.value === outgoing);
  });

  /**
   * Usernames currently spoken for by a row. A signal rather than a read of the FormArray, because a
   * FormControl value changing is invisible to the computeds below — without this, choosing someone in
   * one row would leave them still offered in the others.
   */
  private readonly takenUsernames = signal<string[]>([]);

  /**
   * The options for ONE employee row: everyone not already spoken for, PLUS this row's own current
   * selection.
   *
   * That last part is not a nicety. `p-select` resolves its display label by finding the option whose
   * `optionValue` matches the control's value — so a row whose own user is filtered out of its own
   * options renders blank, which is what a populated list used to look like on load.
   */
  usersForRow(currentUsername: string | null): BankAccountAssignableUser[] {
    const owner = this.selectedOwner();
    const takenByOthers = new Set(this.takenUsernames().filter((u) => u !== currentUsername));
    return this.assignableUsers().filter((u) => (!takenByOthers.has(u.username) && u.username !== owner) || u.username === currentUsername);
  }

  /**
   * Users still free to add. Filtered against the SELECTED owner, not the saved one: mid-transfer the
   * outgoing owner becomes a legitimate employee (that is how they keep access), and the incoming one
   * stops being one.
   */
  readonly availableUsers = computed(() => {
    const taken = new Set(this.takenUsernames());
    const owner = this.selectedOwner();
    return this.assignableUsers().filter((u) => !taken.has(u.username) && u.username !== owner);
  });

  /** Re-reads the rows after anything that could change who is spoken for. */
  private refreshTakenUsernames(): void {
    this.takenUsernames.set(
      this.accessForm.controls.employeeAccess.controls.map((row) => row.controls.username.value).filter((u): u is string => !!u),
    );
  }

  /** Bound to each row's select so the other rows' option lists react to this one's choice. */
  onEmployeeSelected(): void {
    this.refreshTakenUsernames();
  }

  /** Adds the outgoing owner to the employee list, so a transfer need not cost them their own access. */
  keepOutgoingOwnerAccess(): void {
    const outgoing = this.outgoingOwner();
    if (!outgoing) return;
    this.addEmployeeRow(outgoing, [BankAccountAccessRight.FULL_ACCESS]);
    this.accessForm.markAsDirty();
  }

  private readonly employeeRowsSignal = signal<FormGroup<BankAccountEmployeeAccessFormGroup>[]>([]);
  readonly employeeRows = this.employeeRowsSignal.asReadonly();

  addEmployeeRow(username: string | null = null, access: BankAccountAccessRight[] = []): void {
    const row = new FormGroup<BankAccountEmployeeAccessFormGroup>({
      username: new FormControl<string | null>(username, [Validators.required]),
      access: new FormControl<BankAccountAccessRight[]>(access, { nonNullable: true }),
    });
    this.accessForm.controls.employeeAccess.push(row);
    this.syncEmployeeRows();
    this.refreshTakenUsernames();
  }

  removeEmployeeRow(index: number): void {
    this.accessForm.controls.employeeAccess.removeAt(index);
    this.accessForm.markAsDirty();
    this.syncEmployeeRows();
    this.refreshTakenUsernames();
  }

  private syncEmployeeRows(): void {
    this.employeeRowsSignal.set([...this.accessForm.controls.employeeAccess.controls]);
  }

  /** Rebuilds the form from the loaded account. Only the owner is ever sent a populated list. */
  private patchAccessForm(account: BankAccount): void {
    this.accessForm.controls.ownerUsername.setValue(account.ownerUsername ?? null, { emitEvent: false });
    this.selectedOwner.set(account.ownerUsername ?? null);
    this.accessForm.controls.employeeAccess.clear();
    (account.employeeAccess ?? []).forEach((e) => this.addEmployeeRow(e.username, e.access ?? []));
    this.accessForm.markAsPristine();
    this.syncEmployeeRows();
    this.refreshTakenUsernames();
  }

  /** Kept in step with the owner select so the transfer-dependent computeds above stay live. */
  onOwnerSelected(username: string | null): void {
    this.selectedOwner.set(username);
  }

  saveAccess(): void {
    const id = this.currentId();
    if (!id) return;

    if (this.accessForm.invalid) {
      this.accessForm.markAllAsTouched();
      GlobalMessageUtil.error(
        this.t('BankAccount_Access_Title', 'Account access'),
        this.t('BankAccount_Access_PickUser', 'Choose a user for every row, or remove the empty ones.'),
      );
      return;
    }

    // Handing the account to someone else is not undoable from this side — afterwards only they can
    // hand it back — so it is confirmed, and the message says plainly what the current user loses.
    if (this.isTransferringOwnership()) {
      const newOwner = this.assignableUsers().find((u) => u.username === this.accessForm.controls.ownerUsername.value);
      const message = this.transferLosesOwnAccess()
        ? this.t(
            'BankAccount_Access_TransferConfirmLosing',
            'Make {{user}} the owner of this account? You will lose ownership and all access to it, including its balance, and only {{user}} will be able to give it back.',
          )
        : this.t(
            'BankAccount_Access_TransferConfirm',
            'Make {{user}} the owner of this account? You will keep the access listed below, but only {{user}} will be able to change ownership from then on.',
          );

      this.confirmationService.confirm({
        header: this.t('BankAccount_Access_TransferTitle', 'Transfer ownership'),
        message: message.replace(/{{user}}/g, newOwner?.displayName ?? this.accessForm.controls.ownerUsername.value ?? ''),
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.submitAccess(id),
      });
      return;
    }

    this.submitAccess(id);
  }

  private submitAccess(id: string): void {
    const employeeAccess: BankAccountEmployeeAccess[] = this.accessForm.controls.employeeAccess.controls.map((row) => ({
      username: row.controls.username.value!,
      access: row.controls.access.value,
    }));

    this.savingAccess.set(true);
    this.bankAccountService
      .updateAccess(id, { ownerUsername: this.accessForm.controls.ownerUsername.value!, employeeAccess })
      .pipe(finalize(() => this.savingAccess.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) return;
          const saved = response.data;
          this.account.set(saved);
          this.patchAccessForm(saved);

          // A transfer just removed this user's own access to the tab they are standing in. Move them
          // somewhere that still renders rather than leaving them on a panel that has vanished.
          if (!saved.isOwner) {
            this.activeTab.set(saved.canViewBalance ? 'overview' : 'details');
            GlobalMessageUtil.success(
              this.t('BankAccount_Access_Title', 'Account access'),
              this.t('BankAccount_Access_Transferred', 'Ownership transferred. You are no longer the owner of this account.'),
            );
            return;
          }

          GlobalMessageUtil.success(
            this.t('BankAccount_Access_Title', 'Account access'),
            this.t('BankAccount_Access_Saved', 'Access updated.'),
          );
        },
      });
  }

  private loadAssignableUsers(): void {
    this.bankAccountService.getAssignableUsers().subscribe({
      next: (response) => this.assignableUsers.set(response.data ?? []),
      error: () => this.assignableUsers.set([]),
    });
  }

  /**
   * The GL account is fixed once it has been saved — transactions on this bank account already post
   * to it, so repointing it would strand them. Read from the loaded account rather than the form, so
   * choosing one on an unsaved account can still be corrected before it is committed.
   */
  readonly glAccountLocked = computed(() => !!this.account()?.glAccountReference?.reference);

  /**
   * Currency is fixed once the account exists: every transaction and balance already recorded is
   * denominated in it, and re-denominating them is not something a form field can do.
   */
  readonly currencyLocked = computed(() => !!this.account());

  // ---------- access ----------
  /**
   * Whether the API sent real figures for this account. True while creating, where there is no
   * account to be restricted by and the creator becomes the owner on save.
   */
  readonly canViewBalance = computed(() => this.account()?.canViewBalance ?? true);
  /** The owner alone edits the account and manages who else may reach it. */
  readonly isOwner = computed(() => this.account()?.isOwner ?? true);

  // ---------- header / snapshot ----------
  // null throughout rather than 0: these feed a banner, a progress bar and a comparison, and each of
  // them states something false when a withheld balance is read as an empty one.
  readonly currentBalance = computed(() => this.account()?.currentBalance ?? null);
  readonly asOfDate = computed(() => {
    const lines = this.overviewLines();
    if (lines.length) return lines.reduce((max, l) => (l.postingDate > max ? l.postingDate : max), lines[0].postingDate);
    return new Date().toISOString();
  });

  // ---------- thresholds ----------
  readonly minimumBalance = computed(() => this.account()?.minimumBalance ?? null);
  readonly maximumBalance = computed(() => this.account()?.maximumBalance ?? null);
  readonly isBelowMinimum = computed(() => {
    const acc = this.account();
    const current = this.currentBalance();
    const min = this.minimumBalance();
    // Unanswerable without both figures. False is the honest answer — the alternative raises a
    // shortfall alarm on numbers the viewer was never shown.
    if (!acc || current === null || min === null) return false;
    return acc.status === BankAccountStatus.ACTIVE && current < min;
  });
  readonly belowMinimumBy = computed(() => {
    const current = this.currentBalance();
    const min = this.minimumBalance();
    if (current === null || min === null) return null;
    return Math.max(0, min - current);
  });
  readonly thresholdPercent = computed(() => {
    const min = this.minimumBalance();
    const max = this.maximumBalance();
    const current = this.currentBalance();
    if (min === null || max === null || current === null) return null;
    if (max <= min) return current >= min ? 100 : 0;
    return Math.min(100, Math.max(0, ((current - min) / (max - min)) * 100));
  });

  // ---------- month KPIs (current calendar month) ----------
  readonly monthLabel = computed(() => new Date().toLocaleString(undefined, { month: 'long' }));
  private readonly monthLines = computed(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return this.overviewLines().filter((l) => {
      const d = new Date(l.postingDate);
      return d.getFullYear() === y && d.getMonth() === m;
    });
  });
  readonly inflow = computed(() =>
    this.monthLines()
      .filter((l) => l.direction === 'CREDIT')
      .reduce((t, l) => t + l.amount, 0),
  );
  readonly outflow = computed(() =>
    this.monthLines()
      .filter((l) => l.direction === 'DEBIT')
      .reduce((t, l) => t + l.amount, 0),
  );
  readonly net = computed(() => this.inflow() - this.outflow());
  readonly inflowCount = computed(() => this.monthLines().filter((l) => l.direction === 'CREDIT').length);
  readonly outflowCount = computed(() => this.monthLines().filter((l) => l.direction === 'DEBIT').length);

  // ---------- transactions (sorted newest-first) ----------
  /** Recent list (Overview) — newest-first over the bounded overview window. */
  private readonly sortedOverviewLines = computed(() => [...this.overviewLines()].sort((a, b) => (a.postingDate < b.postingDate ? 1 : -1)));
  readonly recentLines = computed(() => this.sortedOverviewLines().slice(0, 5));
  /** Transactions tab — newest-first over the lazily-loaded full history, then client-side filtered. */
  private readonly sortedTransactionLines = computed(() =>
    [...this.transactionLines()].sort((a, b) => (a.postingDate < b.postingDate ? 1 : -1)),
  );
  /**
   * Whether voided movements are listed. Off by default.
   *
   * <p>A void is not a transaction that happened — it is one that was recorded and then retired, usually
   * because a payment was put back to draft. The bank's own statement will never show it, so leaving these
   * in by default would make this screen disagree with the document it exists to be reconciled against.</p>
   *
   * <p>Filtered client-side rather than refetched: the transactions tab already pulls the full history once
   * and caches it, so the rows are in hand and the toggle is instant. The fetch asks for them, the filter
   * decides whether to show them.</p>
   */
  readonly showVoided = signal(false);

  readonly voidedCount = computed(() => this.transactionLines().filter((l) => l.isVoided).length);

  toggleVoided(): void {
    this.showVoided.update((on) => !on);
  }

  readonly filteredLines = computed(() => {
    const term = (this.txnSearch() ?? '').trim().toLowerCase();
    const type = this.txnType();
    const range = this.txnDateRange();
    const from = range?.[0] ? new Date(range[0].getFullYear(), range[0].getMonth(), range[0].getDate()).getTime() : null;
    const to = range?.[1] ? new Date(range[1].getFullYear(), range[1].getMonth(), range[1].getDate(), 23, 59, 59).getTime() : null;
    const showVoided = this.showVoided();
    return this.sortedTransactionLines().filter((l) => {
      if (l.isVoided && !showVoided) return false;
      const matchesTerm =
        !term ||
        (l.number ?? '').toLowerCase().includes(term) ||
        (l.concept ?? '').toLowerCase().includes(term) ||
        (l.description ?? '').toLowerCase().includes(term);
      const matchesType = type === null || type === undefined || l.movementType === type;
      const time = new Date(l.postingDate).getTime();
      const matchesFrom = from === null || time >= from;
      const matchesTo = to === null || time <= to;
      return matchesTerm && matchesType && matchesFrom && matchesTo;
    });
  });

  constructor() {
    this.loadEnumOptions();
    this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).subscribe((companyId) => this.companyReference.set(companyId));
  }

  ngOnInit(): void {
    // Subscribed rather than read off the snapshot: the router reuses this component between
    // `?id=<id>` and `?id=new`, so the state has to be rebuilt on every emission.
    this.loadAssignableUsers();
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id')?.trim();
      if (!id) {
        this.router.navigate(['/bank-accounts']);
        return;
      }
      this.resetLoadedState();
      if (id !== 'new') {
        this.isNew.set(false);
        this.currentId.set(id);
        this.activeTab.set('overview');
        this.loadById(id);
      } else {
        this.isNew.set(true);
        this.currentId.set(null);
        this.headerTitle.set(this.t('BankAccount_Details_New', 'New Bank Account'));
        this.breadcrumbService.setCurrent('Common_New');
        this.bankAccountForm.reset({
          type: BankAccountType.CHECKING,
          currency: Currency.HNL,
          status: BankAccountStatus.ACTIVE,
          currentBalance: 0,
          minimumBalance: 0,
          maximumBalance: 0,
        });
        // Only creation gets to choose an owner.
        this.bankAccountForm.controls.ownerUsername.enable({ emitEvent: false });
        this.accessForm.controls.employeeAccess.clear();
        this.syncEmployeeRows();
        this.refreshTakenUsernames();
        this.activeTab.set('details');
      }
    });
  }

  private resetLoadedState(): void {
    this.account.set(null);
    this.overviewLines.set([]);
    this.transactionLines.set([]);
    this.draftTransactions.set([]);
    this.transactionsLoaded = false;
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  // ---------- enums ----------
  private loadEnumOptions(): void {
    this.typeLabels = this.enumService.getEnum('BankAccountType').values as Record<number, string>;
    this.statusLabels = this.enumService.getEnum('BankAccountStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.movementLabels = this.enumService.getEnum('BankTransactionMovementType').values as Record<number, string>;
    this.typeOptions.set(this.enumToOptions(this.typeLabels));
    this.statusOptions.set(this.enumToOptions(this.statusLabels));
    this.currencyOptions.set(this.enumToOptions(this.currencyLabels));
    this.movementTypeOptions.set(this.enumToOptions(this.movementLabels));
  }

  private enumToOptions(labels: Record<number, string>): EnumOption[] {
    return Object.entries(labels).map(([key, label]) => ({ label, value: Number(key) }));
  }

  // ---------- load ----------
  private loadById(id: string): void {
    this.startLoading();
    this.bankAccountService
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const data = response.data;
          if (!data) return;
          this.account.set(data);
          this.headerTitle.set(data.name ?? data.code ?? this.t('BankAccount_Details_Breadcrumb_Details', 'Details'));
          this.breadcrumbService.setCurrent(data.code);
          this.bankAccountForm.patchValue({
            ...data,
            glAccountReferenceId: data.glAccountReference?.reference ?? null,
          });
          // Ownership is set once, at creation. Disabled rather than hidden so the account still
          // says who administers it — which is the thing a non-owner most needs to know.
          this.bankAccountForm.controls.ownerUsername.disable({ emitEvent: false });
          this.patchAccessForm(data);
          this.loadOverview();
          this.loadDrafts(data);
        },
        error: (error) => {
          console.error('Error fetching bank account:', error);
        },
      });
  }

  /** Overview tab: pull a bounded recent window (covers current-month KPIs + the recent list). */
  private loadOverview(): void {
    const id = this.currentId();
    if (!id) return;
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - BankAccountDetailsComponent.OVERVIEW_WINDOW_DAYS);
    this.startLoading();
    // `to` is sent as a full timestamp (not date-only) so transactions posted today are included.
    this.bankAccountService
      .getStatement(id, this.toIsoDate(from), to.toISOString())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.overviewLines.set(response.data?.lines ?? []),
        error: () => this.overviewLines.set([]),
      });
  }

  /** Transactions tab: pull the full history on first open, then cache (no refetch on tab switch). */
  private loadTransactions(account: BankAccount): void {
    const id = this.currentId();
    if (!id) return;
    const to = new Date();
    const from = account.initialBalanceDate
      ? new Date(account.initialBalanceDate)
      : new Date(to.getFullYear() - 1, to.getMonth(), to.getDate());
    this.startLoading();
    // Voided movements come down with the rest and are filtered out for display. The overview window
    // deliberately does NOT ask for them: its recent list and its KPIs are meant to read like the bank's
    // own statement, and a retired movement is not on it.
    this.bankAccountService
      .getStatement(id, this.toIsoDate(from), to.toISOString(), true)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.transactionLines.set(response.data?.lines ?? []);
          this.transactionsLoaded = true;
        },
        error: () => this.transactionLines.set([]),
      });
  }

  /** Drafts for this account, newest first. Paged high rather than unbounded — a hundred unposted
   *  movements on one account is already a problem worth seeing in full. */
  private loadDrafts(account: BankAccount): void {
    if (!account.code) return;
    this.startLoading();
    this.bankTransactionService
      .search({ bankAccountCode: account.code, statuses: [BankTransactionStatus.DRAFT] }, 1, 100, false, 'transactionDate', -1)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.draftTransactions.set(response.data?.items ?? []),
        error: () => this.draftTransactions.set([]),
      });
  }

  /**
   * Opens a draft where it can actually be finished.
   *
   * <p>A transfer's outgoing leg is a draft movement like any other, but it is owned by an InternalTransfer
   * (#5088) — its accounts and amount come from that document, and the movement screen would happily let
   * someone edit them out from under it. So a leg goes to its transfer instead.</p>
   *
   * <p>Checked by entity code, not by the owner key merely being set (#5140). Since that key became general
   * it can name an AP payment or an advance, and sending one of those to the transfer screen would open a
   * page looking up an id that is not a transfer.</p>
   */
  openDraft(draft: BankTransaction): void {
    const owner = draft.ownerKey;
    if (owner?.reference && owner.entityDefinitionCode === INTERNAL_TRANSFER_ENTITY_CODE) {
      this.router.navigate(['/internal-transfer-details'], { queryParams: { id: owner.reference } });
      return;
    }
    this.router.navigate(['/bank-transaction-details'], { queryParams: { id: draft.id } });
  }

  /** The amount as it affects THIS account, which is the only figure a draft row can meaningfully show. */
  draftAmount(draft: BankTransaction): number {
    const code = this.account()?.code;
    if (draft.to?.code === code) return draft.to?.amount ?? 0;
    if (draft.from?.code === code) return -(draft.from?.amount ?? 0);
    return 0;
  }

  /**
   * A calendar date, formatted from its LOCAL parts.
   *
   * <p>`toISOString()` formats in UTC, which is the same mistake this screen's date handling exists to
   * avoid from the other direction. It happens to agree for us — Honduras is UTC-6, so local midnight is
   * 06:00Z on the same day — but it is only ever right for a viewer behind UTC, and it is silently a day
   * early for one ahead of it.</p>
   */
  private toIsoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // ---------- tabs ----------
  setTab(tab: BankTab): void {
    this.activeTab.set(tab);
    // Defer the full-history fetch until the user actually opens the Transactions tab.
    if (tab === 'transactions' && !this.transactionsLoaded) {
      const account = this.account();
      if (account) this.loadTransactions(account);
    }
  }

  // ---------- save ----------
  save(): void {
    if (this.bankAccountForm.invalid) {
      this.bankAccountForm.markAllAsTouched();
      reportInvalidFormFields(this.bankAccountForm, this.fieldLabels, this.translate);
      return;
    }

    const formValue = this.bankAccountForm.getRawValue();
    const companyReference = this.companyReference();
    if (!companyReference) {
      console.error('No financial company resolved for the selected site; cannot save bank account.');
      GlobalMessageUtil.error(
        this.t('Error', 'Error'),
        this.t('BankAccount_Details_NoCompany', 'No financial company is resolved for the selected site.'),
      );
      return;
    }

    const existing = this.account();
    const payload: BankAccount = {
      ...formValue,
      // No longer captured on this form, but still round-tripped: the API does not preserve either
      // field on update (unlike Code or CurrentBalance), so omitting them would reset the opening
      // balance and lose the date the transactions tab uses as its statement start.
      initialBalance: existing?.initialBalance ?? 0,
      initialBalanceDate: existing?.initialBalanceDate ?? new Date().toISOString(),
      // Editing requires the body id to match the URL id (backend guards against mismatch).
      id: this.currentId() ?? undefined,
      financialCompanyReference: {
        reference: companyReference,
        entityDefinitionCode: FINANCIAL_COMPANY_ENTITY_CODE,
      },
      glAccountReference: formValue.glAccountReferenceId
        ? {
            reference: formValue.glAccountReferenceId,
            entityDefinitionCode: 0, // FinancialAccount
          }
        : null,
    } as unknown as BankAccount;
    delete (payload as { glAccountReferenceId?: string }).glAccountReferenceId;

    this.startLoading();
    const id = this.currentId();
    const request = this.isNew() || !id ? this.bankAccountService.create(payload) : this.bankAccountService.update(id, payload);

    request.pipe(finalize(() => this.stopLoading())).subscribe({
      next: () => {
        GlobalMessageUtil.success(this.t('Success', 'Success'), this.t('BankAccount_Details_Saved', 'Bank account saved.'));
        this.back();
      },
      error: (error) => {
        console.error('Error saving bank account:', error);
      },
    });
  }

  // ---------- transactions actions ----------

  readonly isExporting = signal(false);

  /**
   * The transactions tab as a workbook, over the toolbar's date range.
   *
   * <p><b>Re-queried rather than exported from the rows in hand.</b> The tab holds the whole history and
   * filters it client-side, so the rows are right there — but the workbook is built by the API, like every
   * other export here, and building it from the screen would mean shipping an xlsx writer to the browser
   * for one button.</p>
   *
   * <p><b>Dates, not timestamps.</b> The range goes as two calendar days and the API widens each to the UTC
   * instants it spans — see BooksCalendar. Sending an instant would decide the boundary here, where the
   * offset between a Honduran day and a UTC one has already been lost.</p>
   *
   * <p>With no range picked, the export covers the same window the tab itself loaded: the account's opening
   * balance date through today.</p>
   */
  exportTransactions(): void {
    const id = this.currentId();
    const account = this.account();
    if (!id || !account || this.isExporting()) return;

    const range = this.txnDateRangeControl.value;
    // A half-picked range is a real state of the picker — treat one chosen day as that single day rather
    // than quietly exporting to today.
    const start = range?.[0] ?? null;
    const end = range?.[1] ?? start;

    const to = end ?? new Date();
    const from =
      start ??
      (account.initialBalanceDate ? new Date(account.initialBalanceDate) : new Date(to.getFullYear() - 1, to.getMonth(), to.getDate()));

    const fromDay = this.toIsoDate(from);
    const toDay = this.toIsoDate(to);

    this.isExporting.set(true);
    this.bankAccountService
      .exportStatement(id, fromDay, toDay, this.showVoided())
      .pipe(finalize(() => this.isExporting.set(false)))
      .subscribe({
        next: (blob) => this.triggerDownload(blob, `bank-statement_${account.code ?? id}_${fromDay}_${toDay}.xlsx`),
      });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Starts a movement against THIS account. Direction has to be chosen here: it is fixed once the form
   * opens, and the account travels with it so the user is not asked to re-pick the one they were looking at.
   */
  addTransaction(type: 'INCOME' | 'OUTCOME'): void {
    this.router.navigate(['/bank-transaction-details'], {
      queryParams: { id: 'new', type, account: this.account()?.code ?? null },
    });
  }

  /**
   * Starts an internal transfer OUT of this account (#5089). The source travels in the query string, so the
   * first screen only has to ask which account is receiving — the one thing it cannot know.
   *
   * Not a movement: a transfer takes days, so it books through Cash in Transit and gets its own document
   * pairing the two legs. `addTransaction` above is for money that has already fully arrived or left.
   */
  startInternalTransfer(): void {
    this.router.navigate(['/internal-transfer-details'], {
      queryParams: { from: this.account()?.code ?? null },
    });
  }

  // ---------- labels / helpers ----------
  typeLabel(type: number | null | undefined): string {
    return type === null || type === undefined ? '' : (this.typeLabels[type] ?? '');
  }

  statusLabel(status: number | null | undefined): string {
    return status === null || status === undefined ? '' : (this.statusLabels[status] ?? '');
  }

  currencyLabel(currency: number | null | undefined): string {
    return currency === null || currency === undefined ? '' : (this.currencyLabels[currency] ?? '');
  }

  movementTypeLabel(movementType: number): string {
    return this.movementLabels[movementType] ?? '';
  }

  statusSeverity(status: number | null | undefined): 'success' | 'warn' | 'secondary' | 'info' {
    switch (status) {
      case BankAccountStatus.ACTIVE:
        return 'success';
      case BankAccountStatus.BLOCKED:
        return 'warn';
      case BankAccountStatus.INACTIVE:
        return 'secondary';
      default:
        return 'info';
    }
  }

  movementSeverity(movementType: number): 'success' | 'info' | 'warn' {
    switch (movementType) {
      case BankTransactionMovementType.INCOME:
        return 'success';
      case BankTransactionMovementType.OUTCOME:
        return 'info';
      default:
        return 'warn';
    }
  }

  /** Signed amount for a statement line: CREDIT (in) positive, DEBIT (out) negative. */
  signedAmount(line: BankStatementLine): number {
    return line.direction === 'CREDIT' ? line.amount : -line.amount;
  }

  back(): void {
    this.router.navigate(['/bank-accounts']);
  }
}
